import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import i18n from 'i18next';
import { MeetingRepository } from 'repository/meetingRepository';
import { useSkipIfCreated } from 'hooks/useSkipIfCreated';
import { isNativeApp, openNativeGooglePlaceSearch } from 'common/webview';
import { MeetingProgress } from 'components/meeting/MeetingProgress';
import {
    AppScreen, Button, ButtonFooter, Calendar, InfoGrid, PageIntro,
    Section, SectionHeader, toDateKey,
} from 'components/ds';
import styles from './TravelPlanStep.module.css';
import { toLocalISOString } from 'common/utils/datetime';


const STORAGE_KEY = 'meeting_create_data';

interface PlaceInfo {
    name: string;
    address: string;
    lat?: number;
    lng?: number;
}

export const TravelPlanStep = () => {
    useSkipIfCreated();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [isCreating, setIsCreating] = useState(false);
    const [showCreateConfirm, setShowCreateConfirm] = useState(false);

    // sessionStorage에서 저장된 데이터 로드
    const loadSavedData = () => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : {};
    };

    const savedData = loadSavedData();

    // 여행 기간
    const [startDate, setStartDate] = useState<Date | null>(
        savedData.travelStartDate ? new Date(savedData.travelStartDate) : null
    );
    const [endDate, setEndDate] = useState<Date | null>(
        savedData.travelEndDate ? new Date(savedData.travelEndDate) : null
    );

    // 여행지 정보 (TravelDestination에서 선택된 정보)
    const destination = savedData.destination || '';
    const destinationInfo = savedData.destinationInfo || null;

    // 모임 장소 (집결지)
    const [meetingPlace, setMeetingPlace] = useState<PlaceInfo | null>(savedData.meetingPlace || null);

    // 숙박 호텔
    const [hotelPlace, setHotelPlace] = useState<PlaceInfo | null>(savedData.hotelPlace || null);

    // 숙소가 모임 장소와 동일한지
    const [isSameAsMeetingPlace, setIsSameAsMeetingPlace] = useState(savedData.isSameAsMeetingPlace || false);

    // 모임 시간
    const [meetingTime, setMeetingTime] = useState<string | null>(savedData.meetingTime || null);
    const [isCustomTime, setIsCustomTime] = useState(savedData.isCustomTime || false);

    // 달력 관련
    const today = new Date();
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
    const [selectingDateType, setSelectingDateType] = useState<'start' | 'end' | null>(null);

    // 시간 옵션 — 언어별로 라벨 자동 치환
    const timeOptions = [
        { value: '06:00', label: t('travelPlan.timeAm', { h: 6 }) },
        { value: '07:00', label: t('travelPlan.timeAm', { h: 7 }) },
        { value: '08:00', label: t('travelPlan.timeAm', { h: 8 }) },
        { value: '09:00', label: t('travelPlan.timeAm', { h: 9 }) },
        { value: '10:00', label: t('travelPlan.timeAm', { h: 10 }) },
        { value: '11:00', label: t('travelPlan.timeAm', { h: 11 }) },
        { value: '12:00', label: t('travelPlan.timeNoon') },
        { value: '13:00', label: t('travelPlan.timePm', { h: 1 }) },
        { value: '14:00', label: t('travelPlan.timePm', { h: 2 }) },
        { value: '15:00', label: t('travelPlan.timePm', { h: 3 }) },
        { value: '16:00', label: t('travelPlan.timePm', { h: 4 }) },
        { value: '17:00', label: t('travelPlan.timePm', { h: 5 }) },
        { value: '18:00', label: t('travelPlan.timePm', { h: 6 }) },
    ];

    // location.state에서 장소 검색 결과 또는 시간 선택 결과 받기
    useEffect(() => {
        // 장소 검색 결과
        if (location.state?.selectedPlace && location.state?.placeInfo) {
            const placeInfo: PlaceInfo = {
                name: location.state.placeInfo.name || location.state.selectedPlace,
                address: location.state.placeInfo.address || '',
                lat: location.state.placeInfo.lat,
                lng: location.state.placeInfo.lng,
            };

            if (location.state.placeType === 'meeting') {
                setMeetingPlace(placeInfo);
                if (isSameAsMeetingPlace) {
                    setHotelPlace(placeInfo);
                }
            } else if (location.state.placeType === 'hotel') {
                setHotelPlace(placeInfo);
            }
        }

        // 시간 선택 결과 (TimePicker에서 돌아온 경우)
        if (location.state?.selectedTime) {
            setMeetingTime(location.state.selectedTime);
            setIsCustomTime(true);
        }
    }, [location.state]);

    // 숙소 동일 체크 변경 시
    useEffect(() => {
        if (isSameAsMeetingPlace && meetingPlace) {
            setHotelPlace(meetingPlace);
        }
    }, [isSameAsMeetingPlace, meetingPlace]);

    // 여행 기간 계산
    const getTravelDays = () => {
        if (!startDate || !endDate) return 0;
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    };

    // 데이터 변경 시 sessionStorage에 저장
    useEffect(() => {
        const dataToSave = {
            ...savedData,
            travelStartDate: startDate?.toISOString(),
            travelEndDate: endDate?.toISOString(),
            meetingPlace,
            hotelPlace,
            isSameAsMeetingPlace,
            meetingTime,
            isCustomTime,
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    }, [startDate, endDate, meetingPlace, hotelPlace, isSameAsMeetingPlace, meetingTime, isCustomTime]);

    // 날짜 선택 처리
    const handleDateSelect = (date: Date) => {
        if (selectingDateType === 'start') {
            setStartDate(date);
            if (endDate && date > endDate) {
                setEndDate(null);
            }
            setSelectingDateType('end');
        } else if (selectingDateType === 'end') {
            if (startDate && date < startDate) {
                setStartDate(date);
            } else {
                setEndDate(date);
                setSelectingDateType(null);
            }
        }
    };

    // 날짜 포맷
    const formatDate = (date: Date | null) => {
        if (!date) return '';
        return date.toLocaleDateString('ko-KR', {
            month: 'long',
            day: 'numeric',
            weekday: 'short'
        });
    };

    // 여행 기간 텍스트
    const getTravelPeriodText = () => {
        if (!startDate || !endDate) return '';
        const days = getTravelDays();
        const nights = days - 1;
        return t('travelPlan.periodFormat', { nights, days });
    };

    // 장소 검색 이동
    const handlePlaceSearch = async (placeType: 'meeting' | 'hotel') => {
        // 숙박장소는 네이티브 앱에서 항상 Google Maps 로 위임 (해외/국내 무관).
        // 기존 오픈소스(Leaflet) 지도 대신 네이티브 구글지도 사용 — 사용자 경험 일관성.
        // 집결지(meeting)는 기존 플로우 유지 (한국 카카오 / 해외 구글 분기).
        if (placeType === 'hotel' && isNativeApp()) {
            const currentPlace = hotelPlace || meetingPlace;
            const initialLocation = (currentPlace?.lat && currentPlace?.lng)
                ? { lat: currentPlace.lat, lng: currentPlace.lng }
                : (destinationInfo?.lat && destinationInfo?.lng)
                    ? { lat: destinationInfo.lat, lng: destinationInfo.lng }
                    : undefined;
            const result = await openNativeGooglePlaceSearch({
                initialLocation,
                initialQuery: currentPlace?.name,
                confirmButtonText: t('travelPlan.confirmHotelSelect'),
                title: t('travelPlan.hotelTitle'),
                placeholder: t('travelPlan.searchHotel'),
            });
            if (result) {
                setHotelPlace({
                    name: result.name,
                    address: result.address,
                    lat: result.lat,
                    lng: result.lng,
                });
            }
            return;
        }

        navigate('/meeting/place-search', {
            state: {
                returnTo: '/meeting/create/travel',
                placeType,
                /* 장소 검색 화면이 읽는 이름은 placeInfo 입니다 — 다른 호출부도
                   모두 그 이름으로 넘깁니다. 여기만 currentPlace 로 넘기고 있어,
                   숙박을 다시 열면 앞서 고른 곳이 지도에 없었습니다. */
                placeInfo: placeType === 'meeting' ? meetingPlace : hotelPlace,
            }
        });
    };

    // 오늘 날짜인지 확인
    const isStartToday = startDate &&
        startDate.getDate() === today.getDate() &&
        startDate.getMonth() === today.getMonth() &&
        startDate.getFullYear() === today.getFullYear();

    // 해당 시간이 과거인지 확인 (여행 모임은 HH:MM 또는 오전/오후 형식)
    const isTravelTimePast = (timeStr: string): boolean => {
        if (!isStartToday) return false;
        const now = new Date();

        // HH:MM 형식 (preset)
        const hhmm = timeStr.match(/^(\d{2}):(\d{2})$/);
        if (hhmm) {
            const h = parseInt(hhmm[1]);
            const m = parseInt(hhmm[2]);
            return h < now.getHours() || (h === now.getHours() && m <= now.getMinutes());
        }

        // 오전/오후 형식 (custom)
        const ampm = timeStr.match(/(오전|오후)\s*(\d+):(\d+)/);
        if (ampm) {
            let h = parseInt(ampm[2]);
            const m = parseInt(ampm[3]);
            if (ampm[1] === '오후' && h !== 12) h += 12;
            else if (ampm[1] === '오전' && h === 12) h = 0;
            return h < now.getHours() || (h === now.getHours() && m <= now.getMinutes());
        }

        return false;
    };

    // 선택된 시간이 과거이면 초기화
    useEffect(() => {
        if (meetingTime && isTravelTimePast(meetingTime)) {
            setMeetingTime(null);
        }
    }, [startDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const isNextEnabled = startDate && endDate && meetingTime && meetingPlace && (isSameAsMeetingPlace || hotelPlace);

    const handleCreate = async () => {
        if (!isNextEnabled || isCreating) return;

        // 과거 일시 검증
        if (startDate && meetingTime) {
            const meetingDateTime = new Date(startDate);
            const hhmm = meetingTime.match(/^(\d{2}):(\d{2})$/);
            const ampm = meetingTime.match(/(오전|오후)\s*(\d+):(\d+)/);
            if (hhmm) {
                meetingDateTime.setHours(parseInt(hhmm[1]), parseInt(hhmm[2]), 0, 0);
            } else if (ampm) {
                let h = parseInt(ampm[2]);
                const m = parseInt(ampm[3]);
                if (ampm[1] === '오후' && h !== 12) h += 12;
                else if (ampm[1] === '오전' && h === 12) h = 0;
                meetingDateTime.setHours(h, m, 0, 0);
            }
            if (meetingDateTime <= new Date()) {
                showAlert({ message: t('meetingCreate.alertFutureTime') });
                return;
            }
        }

        // 확인 모달 표시 — 실제 생성은 confirmCreate 에서
        setShowCreateConfirm(true);
    };

    // 확인 모달 "만들기" → 실제 API 호출
    const confirmCreate = async () => {
        setShowCreateConfirm(false);
        if (isCreating) return;
        setIsCreating(true);

        try {
            const allData = loadSavedData();

            // 숙소가 집결 장소와 동일한 경우 집결 장소 정보를 숙소 정보로 복사
            const actualHotelPlace = isSameAsMeetingPlace ? meetingPlace : hotelPlace;

            const createData = {
                meetingName: allData.meetingName,
                description: allData.description || '',
                category: 'TRAVEL',
                meetingType: 'TRAVEL',
                // 모임 장소 (집결지)
                placeName: meetingPlace?.name || '',
                placeAddress: meetingPlace?.address || '',
                latitude: meetingPlace?.lat || 0,
                longitude: meetingPlace?.lng || 0,
                // 모임 시간
                meetingTime: meetingTime,
                // 여행 정보
                travelDestination: destination,
                travelType: destinationInfo?.type || allData.travelType || '',  // DOMESTIC or OVERSEAS
                travelRegionId: destinationInfo?.regionId || '',  // 지역 ID (japan, jeju 등)
                travelStartDate: startDate ? toLocalISOString(startDate) : undefined,
                travelEndDate: endDate ? toLocalISOString(endDate) : undefined,
                // 숙박 정보 (집결 장소와 동일한 경우 집결 장소 정보 사용)
                hotelName: actualHotelPlace?.name || '',
                hotelAddress: actualHotelPlace?.address || '',
                hotelLatitude: actualHotelPlace?.lat || 0,
                hotelLongitude: actualHotelPlace?.lng || 0,
                // 기타
                maxMembers: 10,
                isPublic: true,
            };

            const meetingResponse = await MeetingRepository.onPostCreateMeeting(createData);

            const resData = meetingResponse?.data?.data || meetingResponse?.data;
            const meetingId = typeof resData === 'string' ? resData : resData?.meetingId;
            const inviteCode = typeof resData === 'object' ? resData?.inviteCode : '';

            if (meetingId) {
                // sessionStorage는 TravelMeetingShare에서 정리 (데이터 표시 필요)
                setIsCreating(false);
                // 여행 모임 전용 완료 화면으로 이동
                navigate('/meeting/travel-share', {
                    replace: true,
                    state: {
                        meeting: {
                            meetingId,
                            inviteCode: inviteCode || ''
                        }
                    }
                });
            } else {
                setIsCreating(false);
                showAlert({ message: t('travelPlan.alertCreateFailed') });
            }
        } catch (error) {
            console.error('여행 모임 생성 중 오류:', error);
            setIsCreating(false);
            showAlert({ message: t('travelPlan.alertCreateFailed') });
        }
    };

    // 이전/다음 달
    const handlePrevMonth = () => {
        if (currentMonth === 1) {
            setCurrentMonth(12);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (currentMonth === 12) {
            setCurrentMonth(1);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    /** 고른 장소 한 줄 — 집결지 · 숙소가 같은 형태를 씁니다 */
    const PlaceRow = ({
        place, emptyTitle, emptyDesc, onClick,
    }: {
        place: { name: string; address?: string } | null;
        emptyTitle: string;
        emptyDesc: string;
        onClick: () => void;
    }) => (
        <button type="button" className={styles.placeRow} onClick={onClick}>
            <span className={styles.placeBody}>
                {place ? (
                    <>
                        <span className={styles.placeName}>{place.name}</span>
                        {place.address ? <span className={styles.placeAddr}>{place.address}</span> : null}
                    </>
                ) : (
                    <>
                        <span className={styles.placeEmpty}>{emptyTitle}</span>
                        <span className={styles.placeAddr}>{emptyDesc}</span>
                    </>
                )}
            </span>
            <span className={styles.placeAction}>
                {place ? t('travelPlan.change') : t('common.select')}
            </span>
        </button>
    );

    return (
        <AppScreen title={t('travelPlan.headerTitle')} bar="sub" hasFooter>
            <MeetingProgress currentStep={4} isTravel />

            <PageIntro
                title={t('travelPlan.title')}
                desc={<>{t('travelPlan.subtitle1')} {t('travelPlan.subtitle2')}</>}
            />

            {/* 여행 기간 */}
            <Section spacing="tight">
                <SectionHeader title={t('travelPlan.periodTitle')} />
                <div className={styles.chips}>
                    {([
                        { v: 'start' as const, label: t('meetingCreate.departure'), date: startDate },
                        { v: 'end' as const, label: t('meetingCreate.arrival'), date: endDate },
                    ]).map(o => (
                        <button
                            key={o.v}
                            type="button"
                            className={selectingDateType === o.v ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                            onClick={() => setSelectingDateType(o.v)}
                            aria-pressed={selectingDateType === o.v}
                        >
                            {o.label}{o.date ? ` ${formatDate(o.date)}` : ''}
                        </button>
                    ))}
                </div>

                {startDate && endDate && (
                    <p className={styles.periodText}>{getTravelPeriodText()}</p>
                )}

                {selectingDateType && (
                    <div className={styles.calendarBox}>
                        <Calendar
                            value={selectingDateType === 'start'
                                ? (startDate ? toDateKey(startDate) : undefined)
                                : (endDate ? toDateKey(endDate) : undefined)}
                            min={selectingDateType === 'end' && startDate ? startDate : undefined}
                            onChange={(_key, date) => {
                                if (selectingDateType === 'start') {
                                    setStartDate(date);
                                    // 출발이 도착보다 뒤면 도착을 지웁니다
                                    if (endDate && date > endDate) setEndDate(null);
                                    setSelectingDateType('end');
                                } else {
                                    setEndDate(date);
                                    setSelectingDateType(null);
                                }
                            }}
                        />
                    </div>
                )}
            </Section>

            {/* 집결지 */}
            <Section divided>
                <SectionHeader
                    title={t('travelPlan.meetingPlaceTitle')}
                    sub={t('travelPlan.meetingPlaceDesc')}
                />
                <PlaceRow
                    place={meetingPlace}
                    emptyTitle={t('travelPlan.searchPlace')}
                    emptyDesc={t('travelPlan.placeHint')}
                    onClick={() => handlePlaceSearch('meeting')}
                />
            </Section>

            {/* 집결 시간 */}
            <Section divided>
                <SectionHeader
                    title={t('travelPlan.meetingTimeTitle')}
                    sub={t('travelPlan.meetingTimeDesc')}
                />
                <div className={styles.chips}>
                    {timeOptions.map(option => {
                        const past = isTravelTimePast(option.value);
                        const on = !isCustomTime && meetingTime === option.value;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                className={on ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                                onClick={() => {
                                    if (past) return;
                                    setIsCustomTime(false);
                                    setMeetingTime(option.value);
                                }}
                                disabled={past}
                                aria-pressed={on}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        className={styles.chip}
                        onClick={() => navigate('/meeting/time-picker', { state: { returnTo: '/meeting/create/travel' } })}
                    >
                        {t('travelPlan.directInput')}
                    </button>
                </div>
                {isCustomTime && meetingTime && (
                    <div className={styles.picked}>
                        <span className={styles.pickedLabel}>{t('travelPlan.selectedTime')}</span>
                        <span className={styles.pickedValue}>{meetingTime}</span>
                    </div>
                )}
            </Section>

            {/* 숙소 */}
            <Section divided>
                <SectionHeader
                    title={t('travelPlan.hotelTitle')}
                    sub={t('travelPlan.hotelDesc')}
                />
                <button
                    type="button"
                    className={isSameAsMeetingPlace ? `${styles.checkRow} ${styles.checkRowOn}` : styles.checkRow}
                    onClick={() => meetingPlace && setIsSameAsMeetingPlace(!isSameAsMeetingPlace)}
                    disabled={!meetingPlace}
                    aria-pressed={isSameAsMeetingPlace}
                >
                    <span className={styles.check} aria-hidden />
                    {t('travelPlan.sameAsMeetingPlace')}
                </button>

                {isSameAsMeetingPlace && meetingPlace ? (
                    <div className={styles.sameInfo}>
                        <span className={styles.placeName}>{meetingPlace.name}</span>
                        {meetingPlace.address ? (
                            <span className={styles.placeAddr}>{meetingPlace.address}</span>
                        ) : null}
                    </div>
                ) : (
                    <PlaceRow
                        place={hotelPlace}
                        emptyTitle={t('travelPlan.searchHotel')}
                        emptyDesc={t('travelPlan.hotelHint')}
                        onClick={() => handlePlaceSearch('hotel')}
                    />
                )}
            </Section>

            {/* 안내 */}
            <Section divided last>
                <SectionHeader title={t('travelPlan.tipTitle')} />
                <ul className={styles.tips}>
                    <li>{t('travelPlan.tip1')}</li>
                    <li>{t('travelPlan.tip2')}</li>
                    <li>{t('travelPlan.tip3')}</li>
                </ul>
            </Section>

            <ButtonFooter>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    disabled={!isNextEnabled || isCreating}
                    onClick={handleCreate}
                >
                    {isCreating ? t('travelPlan.creating') : t('travelPlan.createButton')}
                </Button>
            </ButtonFooter>

            {/* 만들기 전에 한 번 더 확인 */}
            {showCreateConfirm && (
                <div className={styles.overlay} onClick={() => setShowCreateConfirm(false)}>
                    <div
                        className={styles.sheet}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('travelPlan.confirmTitle')}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.sheetHead}>
                            <h2 className={styles.sheetTitle}>{t('travelPlan.confirmTitle')}</h2>
                        </div>
                        <div className={styles.sheetBody}>
                            <InfoGrid
                                columns={1}
                                items={[
                                    {
                                        label: t('travelPlan.confirmMeeting'),
                                        value: savedData.meetingName || t('travelPlan.defaultMeetingName'),
                                    },
                                    ...(destination
                                        ? [{ label: t('travelPlan.destinationLabel'), value: destination }]
                                        : []),
                                    ...(startDate && endDate
                                        ? [{
                                            label: t('travelPlan.confirmPeriod'),
                                            value: `${startDate.toLocaleDateString(i18n.language, { month: 'long', day: 'numeric' })}`
                                                + ` ~ ${endDate.toLocaleDateString(i18n.language, { month: 'long', day: 'numeric' })}`,
                                        }]
                                        : []),
                                    ...(meetingTime
                                        ? [{ label: t('travelPlan.confirmMeetingTime'), value: meetingTime }]
                                        : []),
                                    ...(meetingPlace
                                        ? [{
                                            label: t('travelPlan.confirmMeetingPlace'),
                                            value: [meetingPlace.name, meetingPlace.address].filter(Boolean).join(' · '),
                                        }]
                                        : []),
                                    ...(hotelPlace && !isSameAsMeetingPlace
                                        ? [{
                                            label: t('travelPlan.confirmHotel'),
                                            value: [hotelPlace.name, hotelPlace.address].filter(Boolean).join(' · '),
                                        }]
                                        : []),
                                ]}
                            />
                            <p className={styles.sheetNote}>{t('travelPlan.confirmNote')}</p>
                            <div className={styles.sheetActions}>
                                <Button
                                    variant="secondary"
                                    size="lg"
                                    block
                                    onClick={() => setShowCreateConfirm(false)}
                                    disabled={isCreating}
                                >
                                    {t('travelPlan.back')}
                                </Button>
                                <Button
                                    variant="primary"
                                    size="lg"
                                    block
                                    onClick={confirmCreate}
                                    disabled={isCreating}
                                >
                                    {isCreating ? t('travelPlan.creating') : t('travelPlan.create')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AppScreen>
    );
};
